import React, { memo, useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import Loading from "ui-components/loading/loading";
import RangeSlider from "ui-components/range-slider/rangeSlider";

export interface AccessorialRevenueForecastProps {
    isLoading: boolean;
    averageDeliveriesPerDay: number;
    averageAccessorialRevenue: number;
    findingsRate: number;
    operationsApprovalRate: number;
    billingApprovalRate: number;
    averageDailyScans: number;
}

const AccessorialRevenueForecast: React.FC<AccessorialRevenueForecastProps> = memo((props) => {
    const [averageAccessorialRevenue, setAverageAccessorialRevenue] = useState<number>(0);
    const [findingsRate, setFindingsRate] = useState<number>(0);
    const [operationsApprovalRate, setOperationsApprovalRate] = useState<number>(0);
    const [billingApprovalRate, setBillingApprovalRate] = useState<number>(0);
    const [averageDailyScans, setAverageDailyScans] = useState<number>(0);

    useEffect(() => {
        setAverageAccessorialRevenue(props.averageAccessorialRevenue);
        setFindingsRate(props.findingsRate);
        setOperationsApprovalRate(props.operationsApprovalRate);
        setBillingApprovalRate(props.billingApprovalRate);
        setAverageDailyScans(props.averageDailyScans)
    }, [props]);

    const getDailyFindings = () => {
        const dailyFindings = averageDailyScans * (findingsRate / 100);
        return +(dailyFindings.toFixed(0));
    }

    const getDailyApprovedAccessorial = () => {
        const dailyFindings = getDailyFindings();
        const dailyOperationsApproved = dailyFindings * (operationsApprovalRate / 100);
        const dailyBillingApproved = dailyOperationsApproved * (billingApprovalRate / 100);
        return +(dailyBillingApproved.toFixed(0));
    }

    const getDailyProjectedImpact = () => {
        const dailyApprovedAccessorials = getDailyApprovedAccessorial();
        return +((dailyApprovedAccessorials * averageAccessorialRevenue).toFixed(0));
    }

    return (
        <div className="xgs-accessorial-revenue-insights__forecast">
            <div className="xgs-accessorial-revenue-insights__forecast__title">
                Forecast and Impact
            </div>
            <div className="xgs-accessorial-revenue-insights__forecast__content">
                <div className="xgs-accessorial-revenue-insights__forecast__content__indicators">
                    <RangeSlider
                        label="Findings Rate (%)"
                        min={0}
                        max={100}
                        step={1}
                        value={findingsRate}
                        originalValue={props.findingsRate}
                        unit="%"
                        onChange={setFindingsRate}
                        showCurrent
                        showOriginal={true}
                    />
                    <RangeSlider
                        label="Operations Approval Rate (%)"
                        min={0}
                        max={100}
                        step={1}
                        value={operationsApprovalRate}
                        originalValue={props.operationsApprovalRate}
                        unit="%"
                        onChange={setOperationsApprovalRate}
                        showCurrent
                        showOriginal={true}
                    />
                    <RangeSlider
                        label="Billing Approval Rate (%)"
                        min={0}
                        max={100}
                        step={1}
                        value={billingApprovalRate}
                        originalValue={props.billingApprovalRate}
                        unit="%"
                        onChange={setBillingApprovalRate}
                        showCurrent
                        showOriginal={true}
                    />
                    <RangeSlider
                        label="Average Revenue Per Accessorial ($)"
                        min={10}
                        max={300}
                        step={1}
                        value={averageAccessorialRevenue}
                        originalValue={props.averageAccessorialRevenue}
                        unit=""
                        onChange={setAverageAccessorialRevenue}
                        showCurrent
                        showOriginal={true}
                    />
                    <RangeSlider
                        label="Average Deliveries Per Day (#)"
                        min={100}
                        max={5000}
                        step={1}
                        value={averageDailyScans}
                        originalValue={props.averageDailyScans}
                        unit=""
                        onChange={setAverageDailyScans}
                        showCurrent
                        showOriginal={true}
                    />
                </div>
                <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact">
                    <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__title">
                        Projected Impact
                    </div>
                    <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content">
                        <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period">
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__title">
                                Estimated Daily Findings
                            </div>
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__value">
                                {props.isLoading ? <Loading isLoading={true}></Loading> : <NumberFormat
                                    value={getDailyFindings()}
                                    decimalScale={2}
                                    displayType={"text"}
                                    thousandSeparator={","}
                                    prefix={""}
                                />}
                            </div>
                        </div>
                        <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period">
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__title">
                                Daily Approved Accessorials
                            </div>
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__value">
                                {props.isLoading ? <Loading isLoading={true}></Loading> : <NumberFormat
                                    value={getDailyApprovedAccessorial()}
                                    decimalScale={2}
                                    displayType={"text"}
                                    thousandSeparator={","}
                                    prefix={""}
                                />}
                            </div>
                        </div>
                        <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period">
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__title">
                                Daily Revenue
                            </div>
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__value">
                                {props.isLoading ? <Loading isLoading={true}></Loading> : <NumberFormat
                                    value={getDailyProjectedImpact()}
                                    decimalScale={2}
                                    displayType={"text"}
                                    thousandSeparator={","}
                                    prefix={"$ "}
                                />}
                            </div>
                        </div>
                        <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period">
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__title">
                                Weekly Revenue
                            </div>
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__value">
                                {props.isLoading ? <Loading isLoading={true}></Loading> : <NumberFormat
                                    value={5 * getDailyProjectedImpact()}
                                    decimalScale={2}
                                    displayType={"text"}
                                    thousandSeparator={","}
                                    prefix={"$ "}
                                />}
                            </div>
                        </div>
                        <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period">
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__title">
                                Monthly Revenue
                            </div>
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__value">
                                {props.isLoading ? <Loading isLoading={true}></Loading> : <NumberFormat
                                    value={20 * getDailyProjectedImpact()}
                                    decimalScale={2}
                                    displayType={"text"}
                                    thousandSeparator={","}
                                    prefix={"$ "}
                                />}
                            </div>
                        </div>
                        <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period">
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__title">
                                Yearly Revenue
                            </div>
                            <div className="xgs-accessorial-revenue-insights__forecast__content__projected-impact__content__period__value">
                                {props.isLoading ? <Loading isLoading={true}></Loading> : <NumberFormat
                                    value={240 * getDailyProjectedImpact()}
                                    decimalScale={2}
                                    displayType={"text"}
                                    thousandSeparator={","}
                                    prefix={"$ "}
                                />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default AccessorialRevenueForecast;
